// SPDX-License-Identifier: AGPL-3.0-or-later
import { useSyncExternalStore } from "react";
import { fromPercent, toPercent } from "@almanac/domain";
import { payeeKey } from "@almanac/importers";
import type { Backend } from "./backend.js";
import { migratePlan, reservePurchases, setLines } from "./budgetTree.js";
import {
  migrateCategory,
  monthlyPay,
  newCustomId,
  parentOf,
  setCustomNames,
  wasRenamed,
  within,
  type CustomCategory,
  type Profile,
} from "./categories.js";
import { feedEntries, pushEntries } from "./events.js";
import { frontedByTransaction, withoutFronted } from "./groups.js";
import { todayIso } from "./figures.js";
import { activeSaves, paymentEvent, reserveAmount } from "./purchases.js";
import {
  uuid,
  type Account,
  type BudgetPlan,
  type CalendarEvent,
  type FeedPrefs,
  type Group,
  type GroupPayment,
  type MonthBundle,
  type NotifyPrefs,
  type Purchase,
  type PushDetail,
  type Snapshot,
  type Transaction,
  DEFAULT_NOTIFY,
  defaultPlan,
} from "./model.js";
import type { PushSettings } from "./api.js";

export interface StoreState extends Snapshot {
  plan: BudgetPlan;
  /** Every transaction as imported, newest first. */
  ledger: Transaction[];
  /**
   * Transactions as the budget counts them, newest first: a payment that
   * covered others in a group is split into your share and the fronted part.
   */
  transactions: Transaction[];
  fingerprints: ReadonlySet<string>;
  saving: number;
  error: string | null;
}

/**
 * Moves data saved before sub-categories existed onto the category tree. It
 * runs on every load and changes nothing the second time; records are
 * rewritten with the new ids the next time they are saved for any reason.
 */
export function migrateSnapshot(snapshot: Snapshot): Snapshot {
  const months = snapshot.months.map((m) =>
    m.transactions.some((t) => wasRenamed(t.category))
      ? { ...m, transactions: m.transactions.map((t) => ({ ...t, category: migrateCategory(t.category) })) }
      : m,
  );
  const learned = Object.fromEntries(Object.entries(snapshot.learned).map(([k, v]) => [k, migrateCategory(v)]));
  const events = snapshot.events.map((e) => (wasRenamed(e.category) ? { ...e, category: migrateCategory(e.category) } : e));
  const plan = snapshot.plan ? migratePlan(snapshot.plan) : null;
  const notify = snapshot.notify ? { ...snapshot.notify, types: { ...DEFAULT_NOTIFY.types, ...snapshot.notify.types } } : DEFAULT_NOTIFY;
  return { ...snapshot, months, learned, events, plan, profile: snapshot.profile ?? null, custom: snapshot.custom ?? [], notify, purchases: snapshot.purchases ?? [], groups: snapshot.groups ?? [] };
}

function derive(snapshot: Snapshot, saving: number, error: string | null): StoreState {
  const ledger = snapshot.months.flatMap((m) => m.transactions).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  const transactions = withoutFronted(ledger, frontedByTransaction(snapshot.groups, ledger, todayIso()));
  return {
    ...snapshot,
    plan: snapshot.plan ?? defaultPlan(),
    ledger,
    transactions,
    fingerprints: new Set(ledger.map((t) => t.fingerprint)),
    saving,
    error,
  };
}

export type NewTransaction = Omit<Transaction, "id" | "accountId">;

/** The unlocked data for this session. Changes show immediately and save in the background. */
export class Store {
  private state: StoreState;
  private readonly listeners = new Set<() => void>();
  private planTimer: ReturnType<typeof setTimeout> | null = null;
  private feedTimer: ReturnType<typeof setTimeout> | null = null;
  private pushTimer: ReturnType<typeof setTimeout> | null = null;
  /** Formats amounts in feed titles; the shell sets it to the reader's locale. */
  money: (cents: number) => string = (cents) => (cents / 100).toFixed(2);

  constructor(
    readonly backend: Backend,
    snapshot: Snapshot,
  ) {
    const migrated = migrateSnapshot(snapshot);
    setCustomNames(migrated.custom);
    this.state = derive(migrated, 0, null);
    // A new month changes what save-up goals need; bring the budget line up to date.
    if (this.reserved(this.state.plan) !== this.state.plan) this.setPlan(this.state.plan);
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = () => this.state;

  private set(snapshot: Partial<Snapshot>, saving = this.state.saving, error = this.state.error) {
    const { accounts, months, plan, learned, events, feed, profile, custom, notify, purchases, groups } = this.state;
    this.state = derive({ accounts, months, plan, learned, events, feed, profile, custom, notify, purchases, groups, ...snapshot }, saving, error);
    if (snapshot.custom) setCustomNames(snapshot.custom);
    this.listeners.forEach((l) => l());
  }

  private track<T>(work: Promise<T>): Promise<T> {
    this.set({}, this.state.saving + 1);
    work.then(
      () => this.set({}, this.state.saving - 1),
      (error: unknown) => this.set({}, this.state.saving - 1, (error as Error).message),
    );
    return work;
  }

  dismissError() {
    this.set({}, this.state.saving, null);
  }

  ensureAccount(name: string, bank: string | null): Account {
    const existing = this.state.accounts.find((a) => a.name.toLowerCase() === name.trim().toLowerCase());
    if (existing) return existing;
    const account: Account = { id: uuid(), name: name.trim(), bank };
    this.set({ accounts: [...this.state.accounts, account] });
    void this.track(this.backend.saveAccount(account));
    return account;
  }

  /** Adds transactions to an account, skipping any whose fingerprint is already stored. */
  addTransactions(accountId: string, items: readonly NewTransaction[]): number {
    const known = new Set(this.state.fingerprints);
    const months = new Map(this.state.months.map((m) => [`${m.accountId}|${m.month}`, m]));
    const changed = new Set<string>();
    let added = 0;
    for (const item of items) {
      if (known.has(item.fingerprint)) continue;
      known.add(item.fingerprint);
      const key = `${accountId}|${item.date.slice(0, 7)}`;
      const bundle = months.get(key) ?? { accountId, month: item.date.slice(0, 7), transactions: [] };
      months.set(key, { ...bundle, transactions: [...bundle.transactions, { ...item, id: uuid(), accountId }] });
      changed.add(key);
      added++;
    }
    this.set({ months: [...months.values()] });
    for (const key of changed) void this.track(this.backend.saveMonth(months.get(key)!));
    return added;
  }

  /** Changes a category. With `learn`, future imports from the same payee get it too. */
  setCategory(transactionId: string, category: string | null, learn = true) {
    this.setCategories([transactionId], category, learn);
  }

  /** Changes several categories at once, saving each affected month once. */
  setCategories(transactionIds: readonly string[], category: string | null, learn = true) {
    const ids = new Set(transactionIds);
    const targets: Transaction[] = [];
    const months = this.state.months.map((m) => {
      if (!m.transactions.some((t) => ids.has(t.id))) return m;
      const next = {
        ...m,
        transactions: m.transactions.map((t) => {
          if (!ids.has(t.id)) return t;
          const changed = { ...t, category };
          targets.push(changed);
          return changed;
        }),
      };
      void this.track(this.backend.saveMonth(next));
      return next;
    });
    const update: Partial<Snapshot> = { months };
    if (learn && category && targets.length > 0) {
      const learned = { ...this.state.learned };
      for (const t of targets) learned[payeeKey(t.description)] = category;
      update.learned = learned;
      void this.track(this.backend.saveLearned(learned));
    }
    this.set(update);
  }

  learn(description: string, category: string) {
    const learned = { ...this.state.learned, [payeeKey(description)]: category };
    this.set({ learned });
    void this.track(this.backend.saveLearned(learned));
  }

  /** Slider changes arrive many times a second; they show at once and save after a pause. */
  setPlan(budget: BudgetPlan) {
    const plan = this.reserved(budget);
    this.set({ plan });
    if (this.planTimer) clearTimeout(this.planTimer);
    this.planTimer = setTimeout(() => void this.track(this.backend.savePlan(plan)), 600);
  }

  /** The plan with the large-purchases line holding what the goals ask for. */
  private reserved(plan: BudgetPlan): BudgetPlan {
    return reservePurchases(plan, reserveAmount(this.state.purchases, plan.purchaseLimit ?? null, todayIso()));
  }

  /**
   * Adds or changes a large purchase. A financed one keeps its monthly
   * payment event (titled `paymentTitle`) in step; the budget line follows.
   */
  savePurchase(purchase: Purchase, paymentTitle: string) {
    let next = purchase;
    if (next.mode === "finance" && !next.eventId) next = { ...next, eventId: uuid() };
    const event = paymentEvent(next, paymentTitle, todayIso());
    let events = this.state.events;
    if (event) {
      events = [...events.filter((e) => e.id !== event.id), event];
      void this.track(this.backend.saveEvent(event));
    } else if (next.eventId) {
      const gone = next.eventId;
      events = events.filter((e) => e.id !== gone);
      void this.track(this.backend.deleteEvent(gone));
      next = { ...next, eventId: null };
    }
    const purchases = [...this.state.purchases.filter((p) => p.id !== next.id), next];
    this.set({ purchases, events });
    void this.track(this.backend.savePurchase(next));
    if (event || purchase.eventId) this.republish();
    this.setPlan(this.state.plan);
    return next;
  }

  /** Removes a purchase and, if it was financed, its payment event. */
  deletePurchase(id: string) {
    const purchase = this.state.purchases.find((p) => p.id === id);
    if (!purchase) return;
    const events = purchase.eventId ? this.state.events.filter((e) => e.id !== purchase.eventId) : this.state.events;
    if (purchase.eventId) void this.track(this.backend.deleteEvent(purchase.eventId));
    this.set({ purchases: this.state.purchases.filter((p) => p.id !== id), events });
    void this.track(this.backend.deletePurchase(id));
    if (purchase.eventId) this.republish();
    this.setPlan(this.state.plan);
  }

  /** Moves a save-up goal up (-1) or down (+1) the priority list. */
  movePurchase(id: string, step: -1 | 1) {
    const list = activeSaves(this.state.purchases);
    const from = list.findIndex((p) => p.id === id);
    const to = from + step;
    if (from < 0 || to < 0 || to >= list.length) return;
    [list[from], list[to]] = [list[to]!, list[from]!];
    const changed = new Map<string, Purchase>();
    list.forEach((p, i) => {
      if (p.priority !== i) changed.set(p.id, { ...p, priority: i });
    });
    this.set({ purchases: this.state.purchases.map((p) => changed.get(p.id) ?? p) });
    for (const p of changed.values()) void this.track(this.backend.savePurchase(p));
    this.setPlan(this.state.plan);
  }

  /** Sets a monthly limit for large purchases, or null to follow what the goals need. */
  setPurchaseLimit(limit: number | null) {
    this.setPlan({ ...this.state.plan, purchaseLimit: limit });
  }

  /** Adds or replaces a group. */
  saveGroup(group: Group) {
    const exists = this.state.groups.some((g) => g.id === group.id);
    this.set({ groups: exists ? this.state.groups.map((g) => (g.id === group.id ? group : g)) : [...this.state.groups, group] });
    void this.track(this.backend.saveGroup(group));
  }

  deleteGroup(id: string) {
    this.set({ groups: this.state.groups.filter((g) => g.id !== id) });
    void this.track(this.backend.deleteGroup(id));
  }

  /**
   * Records a repayment. One matched to an incoming transfer files that
   * transfer as money moved between people, not income.
   */
  recordPayment(groupId: string, payment: Omit<GroupPayment, "id">) {
    const group = this.state.groups.find((g) => g.id === groupId);
    if (!group) return;
    this.saveGroup({ ...group, payments: [...group.payments, { ...payment, id: uuid() }] });
    if (payment.txId) this.setCategory(payment.txId, "transfers-in", false);
  }

  /** Stops offering an incoming transfer as a repayment. */
  declineOffer(groupId: string, txId: string) {
    const group = this.state.groups.find((g) => g.id === groupId);
    if (group) this.saveGroup({ ...group, declined: [...group.declined, txId] });
  }

  /** Saves the setup survey. A pay amount resets the budget pool, keeping each line's share. */
  setProfile(profile: Profile) {
    const update: Partial<Snapshot> = { profile };
    if (profile.pay) {
      const pool = monthlyPay(profile.pay);
      const plan = this.state.plan;
      const lines = plan.lines.map((l) => ({ ...l, amount: fromPercent(toPercent(l.amount, plan.pool), pool) }));
      lines[lines.length - 1]!.amount += pool - lines.reduce((s, l) => s + l.amount, 0);
      update.plan = setLines({ ...plan, pool }, lines);
      void this.track(this.backend.savePlan(update.plan));
    }
    this.set(update);
    void this.track(this.backend.saveProfile(profile));
  }

  /** Adds a category under `parent` and returns it. */
  addCategory(parent: string, name: string): CustomCategory {
    const category = { id: newCustomId(parent), name: name.trim().slice(0, 60) };
    const custom = [...this.state.custom, category];
    this.set({ custom });
    void this.track(this.backend.saveCustomCategories(custom));
    return category;
  }

  /**
   * Removes a custom category and everything under it. Its transactions,
   * learned payees, events and budget all move up to its parent.
   */
  removeCategory(id: string) {
    const parent = parentOf(id)!;
    const move = (c: string | null) => (c !== null && within(c, id) ? parent : c);
    const custom = this.state.custom.filter((c) => !within(c.id, id));
    const months = this.state.months.map((m) => {
      if (!m.transactions.some((t) => t.category !== null && within(t.category, id))) return m;
      const next = { ...m, transactions: m.transactions.map((t) => ({ ...t, category: move(t.category) })) };
      void this.track(this.backend.saveMonth(next));
      return next;
    });
    const learned = Object.fromEntries(Object.entries(this.state.learned).map(([k, v]) => [k, move(v)!]));
    const events = this.state.events.map((e) => {
      if (e.category === null || !within(e.category, id)) return e;
      const next = { ...e, category: parent };
      void this.track(this.backend.saveEvent(next));
      return next;
    });
    // Its budget goes back to the parent's own line.
    const plan = this.state.plan;
    const split = plan.splits[parent];
    const splits = Object.fromEntries(Object.entries(plan.splits).filter(([k]) => !within(k, id)));
    if (split) {
      const freed = split.filter((l) => l.id === id).reduce((s, l) => s + l.amount, 0);
      splits[parent] = split.filter((l) => l.id !== id).map((l) => (l.id === parent ? { ...l, amount: l.amount + freed } : l));
    }
    const nextPlan = { ...plan, splits };
    this.set({ custom, months, learned, events, plan: nextPlan });
    void this.track(this.backend.saveCustomCategories(custom));
    void this.track(this.backend.saveLearned(learned));
    void this.track(this.backend.savePlan(nextPlan));
  }

  /** Adds or changes an event. */
  saveEvent(event: CalendarEvent) {
    const others = this.state.events.filter((e) => e.id !== event.id);
    this.set({ events: [...others, event] });
    void this.track(this.backend.saveEvent(event));
    this.republish();
  }

  deleteEvent(id: string) {
    this.set({ events: this.state.events.filter((e) => e.id !== id) });
    void this.track(this.backend.deleteEvent(id));
    this.republish();
  }

  /** Turns the calendar feed on, or replaces its address so the old one stops working. */
  async startFeed(name: string) {
    const token = await this.track(this.backend.startFeed(name, feedEntries(this.state.events, this.state.feed, this.money)));
    const feed = { ...this.state.feed, token };
    this.set({ feed });
    await this.track(this.backend.saveFeedPrefs(feed));
  }

  async stopFeed() {
    await this.track(this.backend.stopFeed());
    const feed = { ...this.state.feed, token: null };
    this.set({ feed });
    await this.track(this.backend.saveFeedPrefs(feed));
  }

  setFeedDetail(detail: FeedPrefs["detail"]) {
    const feed = { ...this.state.feed, detail };
    this.set({ feed });
    void this.track(this.backend.saveFeedPrefs(feed));
    this.republish();
  }

  /** Shows or hides a kind of notification. */
  setNotifyType(type: keyof NotifyPrefs["types"], on: boolean) {
    this.saveNotify({ ...this.state.notify, types: { ...this.state.notify.types, [type]: on } });
  }

  /** Hides notifications. `current` is every id still showing, so stale ids are dropped as it saves. */
  dismiss(ids: readonly string[], current: readonly string[]) {
    const live = new Set(current);
    const dismissed = [...new Set([...this.state.notify.dismissed, ...ids])].filter((id) => live.has(id));
    this.saveNotify({ ...this.state.notify, dismissed });
  }

  private saveNotify(notify: NotifyPrefs) {
    this.set({ notify });
    void this.track(this.backend.saveNotifyPrefs(notify));
  }

  /** Turns phone notifications on, or changes their settings, and sends the reminder schedule. */
  async savePush(settings: PushSettings, detail: PushDetail) {
    await this.track(this.backend.savePush(settings, pushEntries(this.state.events, detail, this.money)));
    this.saveNotify({ ...this.state.notify, push: { detail } });
  }

  /** Turns phone notifications off; the server forgets the settings and schedule. */
  async stopPush() {
    await this.track(this.backend.stopPush());
    this.saveNotify({ ...this.state.notify, push: null });
  }

  /** After event changes, sends the feed and the phone schedule their new entries (batched, as edits often come in runs). */
  private republish() {
    if (this.state.notify.push) {
      if (this.pushTimer) clearTimeout(this.pushTimer);
      this.pushTimer = setTimeout(() => {
        this.pushTimer = null;
        this.sendPushEntries();
      }, 800);
    }
    if (!this.state.feed.token) return;
    if (this.feedTimer) clearTimeout(this.feedTimer);
    this.feedTimer = setTimeout(() => {
      this.feedTimer = null;
      void this.track(this.backend.updateFeed(feedEntries(this.state.events, this.state.feed, this.money)));
    }, 800);
  }

  private sendPushEntries() {
    const push = this.state.notify.push;
    if (push) return this.track(this.backend.updatePushEntries(pushEntries(this.state.events, push.detail, this.money)));
  }

  /** Saves any pending slider, feed or phone schedule change now, e.g. before locking. */
  async flush() {
    if (this.pushTimer) {
      clearTimeout(this.pushTimer);
      this.pushTimer = null;
      await this.sendPushEntries();
    }
    if (this.planTimer) {
      clearTimeout(this.planTimer);
      this.planTimer = null;
      await this.track(this.backend.savePlan(this.state.plan));
    }
    if (this.feedTimer) {
      clearTimeout(this.feedTimer);
      this.feedTimer = null;
      if (this.state.feed.token) await this.track(this.backend.updateFeed(feedEntries(this.state.events, this.state.feed, this.money)));
    }
  }
}

export function useStore(store: Store): StoreState {
  return useSyncExternalStore(store.subscribe, store.getState);
}
